import React, { useState, useMemo, useEffect } from 'react';
import { 
  Calendar, User, Star, MapPin, DollarSign, TrendingUp,
  ChevronUp, ChevronDown, Search, RotateCcw
} from 'lucide-react';

const DraftTable = ({ 
  data = [], 
  loading = false, 
  error = null,
  title = "Draft Data",
  subtitle = "Detailed player draft information"
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'Year', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [isMobile, setIsMobile] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);
  const [showOrientationHint, setShowOrientationHint] = useState(true);
  
  const recordsPerPageOptions = [5, 10, 25, 50, 100];

  // Check for mobile screen size and orientation
  useEffect(() => {
    const checkScreenProperties = () => {
      const isMobileSize = window.innerWidth < 768;
      const isPortraitOrientation = window.innerHeight > window.innerWidth && isMobileSize;
      
      setIsMobile(isMobileSize);
      setIsPortrait(isPortraitOrientation);
    };
    
    checkScreenProperties();
    window.addEventListener('resize', checkScreenProperties);
    window.addEventListener('orientationchange', checkScreenProperties);
    
    return () => {
      window.removeEventListener('resize', checkScreenProperties);
      window.removeEventListener('orientationchange', checkScreenProperties);
    };
  }, []);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleRecordsPerPageChange = (newRecordsPerPage) => {
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    
    const searchLower = searchTerm.toLowerCase().trim();
    return data.filter(record => 
      record.Player?.toLowerCase().includes(searchLower) ||
      record.Manager?.toLowerCase().includes(searchLower) ||
      record.Position?.toLowerCase().includes(searchLower) ||
      record.Investment?.toLowerCase().includes(searchLower) ||
      record.Year?.toString().includes(searchLower) ||
      record.Cost?.toString().includes(searchLower)
    );
  }, [data, searchTerm]);

  // Sort the filtered data
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (sortConfig.direction === 'asc') {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });
  }, [filteredData, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / recordsPerPage);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  // Reset to page 1 when data changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  // Adjust current page if it exceeds total pages after filtering/searching
  React.useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Mobile Card Component
  const MobileCard = ({ record, index }) => (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
      {/* Header Row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="bg-gray-100 rounded-lg p-2">
            <Star className="h-4 w-4 text-gray-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-base">{record.Player}</h3>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <span>{record.Manager}</span>
              <span>•</span>
              <span>{record.Year}</span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-gray-900">${record.Cost}</div>
        </div>
      </div>

      {/* Tags Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className={'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ' + 
            (record.Position === 'QB' ? 'bg-red-100 text-red-800' :
            record.Position === 'RB' ? 'bg-green-100 text-green-800' :
            record.Position === 'WR' ? 'bg-blue-100 text-blue-800' :
            record.Position === 'TE' ? 'bg-yellow-100 text-yellow-800' :
            record.Position === 'K' ? 'bg-purple-100 text-purple-800' :
            'bg-gray-100 text-gray-800'
          )}>
            {record.Position}
          </span>
          <span className={'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ' + 
            (record.Investment === 'Keeper' ? 'bg-orange-100 text-orange-800' : 'bg-indigo-100 text-indigo-800')
          }>
            {record.Investment}
          </span>
        </div>
      </div>
    </div>
  );

  // Orientation Hint Component
  const OrientationHint = () => (
    <div className={`mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 transition-all duration-300 ${showOrientationHint ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-100 p-2 rounded-lg">
            <svg 
              className="w-5 h-5 text-blue-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" 
              />
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M9 9l3 3m0 0l3-3m-3 3V3"
                className="animate-bounce"
              />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-blue-800">Better Experience Available</p>
            <p className="text-xs text-blue-600">Rotate to landscape for improved table viewing</p>
          </div>
        </div>
        <button
          onClick={() => setShowOrientationHint(false)}
          className="text-blue-400 hover:text-blue-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );

  // Mobile Sort Component
  const MobileSortSelector = () => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Sort by:</label>
      <select
        value={`${sortConfig.key}-${sortConfig.direction}`}
        onChange={(e) => {
          const [key, direction] = e.target.value.split('-');
          setSortConfig({ key, direction });
          setCurrentPage(1);
        }}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
      >
        <option value="Year-desc">Year (Newest First)</option>
        <option value="Year-asc">Year (Oldest First)</option>
        <option value="Player-asc">Player (A-Z)</option>
        <option value="Player-desc">Player (Z-A)</option>
        <option value="Manager-asc">Manager (A-Z)</option>
        <option value="Manager-desc">Manager (Z-A)</option>
        <option value="Cost-desc">Cost (Highest First)</option>
        <option value="Cost-asc">Cost (Lowest First)</option>
        <option value="Position-asc">Position (A-Z)</option>
        <option value="Investment-asc">Investment Type</option>
      </select>
    </div>
  );

  // Mobile Pagination Component
  const MobilePagination = () => (
    <div className="mt-6 space-y-4">
      {/* Page Info */}
      <div className="text-center text-sm text-gray-600">
        Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, sortedData.length)} of {sortedData.length} results
        {searchTerm && ` (filtered from ${data.length} total)`}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-center space-x-2">
        <button
          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Previous
        </button>

        {/* Page indicator */}
        <div className="flex items-center space-x-1">
          {totalPages <= 5 ? (
            // Show all pages if 5 or fewer
            [...Array(totalPages)].map((_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={'px-3 py-2 text-sm font-medium rounded-lg transition-colors ' + 
                    (currentPage === pageNum
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                  )}
                >
                  {pageNum}
                </button>
              );
            })
          ) : (
            // Compact pagination for many pages
            <>
              {currentPage > 2 && (
                <>
                  <button
                    onClick={() => setCurrentPage(1)}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    1
                  </button>
                  {currentPage > 3 && <span className="text-gray-500">...</span>}
                </>
              )}
              
              {[...Array(Math.min(3, totalPages))].map((_, i) => {
                const pageNum = Math.max(1, Math.min(totalPages - 2, currentPage - 1)) + i;
                if (pageNum > totalPages) return null;
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={'px-3 py-2 text-sm font-medium rounded-lg transition-colors ' + 
                      (currentPage === pageNum
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    )}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              {currentPage < totalPages - 1 && (
                <>
                  {currentPage < totalPages - 2 && <span className="text-gray-500">...</span>}
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </>
          )}
        </div>

        <button
          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Next
        </button>
      </div>

      {/* Records per page for mobile */}
      <div className="flex items-center justify-center space-x-2 text-sm">
        <span className="text-gray-600">Show:</span>
        <select
          value={recordsPerPage}
          onChange={(e) => handleRecordsPerPageChange(parseInt(e.target.value))}
          className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          {recordsPerPageOptions.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <span className="text-gray-600">per page</span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">{title}</h2>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">{subtitle}</p>
        </div>
        <div className="p-6 sm:p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">{title}</h2>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">{subtitle}</p>
        </div>
        <div className="p-6 sm:p-8">
          <div className="text-center text-red-600">
            <p className="font-medium">Error loading table data</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">{title}</h2>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">{subtitle}</p>
        </div>
        <div className="p-6 sm:p-8">
          <div className="text-center text-gray-500">
            <p className="font-medium">No draft data available</p>
            <p className="text-sm mt-1">Try adjusting your filters to see results</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">{title}</h2>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">{subtitle}</p>
          </div>
          <div className="mt-2 sm:mt-0 flex items-center space-x-2">
            <span className="text-sm text-gray-500">
              {sortedData.length} of {data.length} records
            </span>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search players, managers, positions..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <RotateCcw className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>

        {/* Search Results Info */}
        {searchTerm && (
          <div className="mb-4 text-sm text-gray-600">
            {filteredData.length === 0 ? (
              <span className="text-red-600">No results found for "{searchTerm}"</span>
            ) : (
              <span>Found {filteredData.length} result{filteredData.length !== 1 ? 's' : ''} for "{searchTerm}"</span>
            )}
          </div>
        )}

        {/* Mobile Sort Selector */}
        {isMobile && <MobileSortSelector />}
        
        {/* Orientation Hint for Portrait Mobile */}
        {isMobile && isPortrait && showOrientationHint && <OrientationHint />}

        {/* Desktop Controls */}
        {!isMobile && (
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            {/* Records Per Page Selector */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Show:
              </label>
              <select
                value={recordsPerPage}
                onChange={(e) => handleRecordsPerPageChange(parseInt(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {recordsPerPageOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-700 whitespace-nowrap">per page</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Mobile Cards View */}
      {isMobile ? (
        <div className="p-4">
          <div className="space-y-3">
            {paginatedData.map((record, index) => (
              <MobileCard key={index} record={record} index={index} />
            ))}
          </div>
          {totalPages > 1 && <MobilePagination />}
        </div>
      ) : (
        /* Desktop Table View */
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[
                    { key: 'Year', label: 'Year', icon: Calendar },
                    { key: 'Manager', label: 'Manager', icon: User },
                    { key: 'Player', label: 'Player', icon: Star },
                    { key: 'Position', label: 'Position', icon: MapPin },
                    { key: 'Cost', label: 'Cost', icon: DollarSign },
                    { key: 'Investment', label: 'Investment', icon: TrendingUp }
                  ].map(column => (
                    <th
                      key={column.key}
                      className="px-6 py-4 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort(column.key)}
                    >
                      <div className="flex items-center space-x-2">
                        <column.icon className="h-4 w-4" />
                        <span>{column.label}</span>
                        {sortConfig.key === column.key && (
                          sortConfig.direction === 'asc' ? 
                            <ChevronUp className="h-4 w-4" /> : 
                            <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedData.map((record, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">{record.Year}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{record.Manager}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">{record.Player}</td>
                    <td className="px-6 py-4">
                      <span className={'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ' + 
                        (record.Position === 'QB' ? 'bg-red-100 text-red-800' :
                        record.Position === 'RB' ? 'bg-green-100 text-green-800' :
                        record.Position === 'WR' ? 'bg-blue-100 text-blue-800' :
                        record.Position === 'TE' ? 'bg-yellow-100 text-yellow-800' :
                        record.Position === 'K' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      )}>
                        {record.Position}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-semibold">${record.Cost}</td>
                    <td className="px-6 py-4">
                      <span className={'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ' + 
                        (record.Investment === 'Keeper' ? 'bg-orange-100 text-orange-800' : 'bg-indigo-100 text-indigo-800')
                      }>
                        {record.Investment}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Desktop Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-700">
                  Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, sortedData.length)} of {sortedData.length} results
                  {searchTerm && ` (filtered from ${data.length} total)`}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  
                  {/* Page Numbers */}
                  <div className="flex items-center space-x-1">
                    {totalPages <= 7 ? (
                      // Show all pages if 7 or fewer
                      [...Array(totalPages)].map((_, i) => {
                        const pageNum = i + 1;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={'px-3 py-2 text-sm font-medium rounded-md transition-colors ' + 
                              (currentPage === pageNum
                                ? 'bg-indigo-600 text-white'
                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                            )}
                          >
                            {pageNum}
                          </button>
                        );
                      })
                    ) : (
                      // Smart pagination for more than 7 pages
                      <>
                        {currentPage > 3 && (
                          <>
                            <button
                              onClick={() => setCurrentPage(1)}
                              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                              1
                            </button>
                            {currentPage > 4 && <span className="px-2 text-gray-500">...</span>}
                          </>
                        )}
                        
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                          const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                          if (pageNum > totalPages) return null;
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={'px-3 py-2 text-sm font-medium rounded-md transition-colors ' + 
                                (currentPage === pageNum
                                  ? 'bg-indigo-600 text-white'
                                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                              )}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        {currentPage < totalPages - 2 && (
                          <>
                            {currentPage < totalPages - 3 && <span className="px-2 text-gray-500">...</span>}
                            <button
                              onClick={() => setCurrentPage(totalPages)}
                              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                              {totalPages}
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DraftTable;